import { createContext, useContext } from 'react';
import type { LocationScope, Tenant } from '../lib/api';
import {
    resolveCapabilitiesFallback,
    type BooleanCapabilityKey,
    type TenantCapabilities,
} from '../lib/capabilities';

export interface Store {
    id: string;
    name: string;
    isActive?: boolean;
    warehouses?: Array<{
        id: string;
        isActive?: boolean;
    }>;
}

export interface TenantContextType {
    tenant: Tenant | undefined;
    tenantId: string | undefined;
    tenantName: string | undefined;
    /** Industry. Copy only (labels/placeholders/examples) — never gate functionality on it. */
    vertical: string | undefined;
    /** Legacy raw feature record. Prefer `capabilities` for functional decisions. */
    features: Record<string, any>;
    /** Effective tenant capabilities (backend-resolved, with a local fallback). */
    capabilities: TenantCapabilities;
    hasCapability: (key: BooleanCapabilityKey) => boolean;
    stores: Store[];
    /** Concrete operating store — POS, Caja and every write always use this one. */
    currentStore: Store | undefined;
    setCurrentStore: (store: Store) => void;
    /** Active stores of the whole business (a cashier only operates one, but others exist). */
    tenantStoreCount: number;
    /** Backend-resolved location scope of the user (null on older payloads). */
    locationScope: LocationScope | null;
    /** OWNER/ADMIN with more than one store: may read "Todas las sucursales". */
    canViewAllStores: boolean;
    /**
     * "Todas las sucursales" chosen in the header (read-only consolidated views
     * such as the Dashboard). Never changes `currentStore`: nothing sells "everywhere".
     */
    isAllStores: boolean;
    setAllStores: () => void;
    refreshUser: () => Promise<void>;
    hasFeature: (featureKey: string) => boolean;
    isVertical: (vertical: string) => boolean;
    isLoading: boolean;
}

export const TenantContext = createContext<TenantContextType | undefined>(undefined);

const fallbackCapabilities = resolveCapabilitiesFallback(undefined, {});

const fallbackTenantContext: TenantContextType = {
    tenant: undefined,
    tenantId: undefined,
    tenantName: undefined,
    vertical: undefined,
    features: {},
    capabilities: fallbackCapabilities,
    hasCapability: (key) => fallbackCapabilities[key] === true,
    stores: [],
    currentStore: undefined,
    setCurrentStore: () => { },
    tenantStoreCount: 0,
    locationScope: null,
    canViewAllStores: false,
    isAllStores: false,
    setAllStores: () => { },
    refreshUser: async () => { },
    hasFeature: () => false,
    isVertical: () => false,
    isLoading: false,
};

export const useTenant = () => {
    const context = useContext(TenantContext);
    return context || fallbackTenantContext;
};

/** Effective tenant capabilities — what the business CAN do. */
export const useCapabilities = (): TenantCapabilities => useTenant().capabilities;
